/**
 * KontrolluSilDugmesi.jsx — #550: the delete button of a Fuar / Firma / Kişi / Stand page (FDS-Backend #549 decided what may be deleted).
 *
 * A fair, a company and a person are deleted only when nothing hangs off them, and the backend answers that BEFORE the delete:
 * GET …/silme-kontrolu → `{ message, data: { silinebilir, engeller: [{ tur, sayi, metin }] } }`, the message in the request's language and
 * naming what is still there. This button asks it once when it appears (`kontrol`):
 *   · 403 / 401 / 404 → nothing is drawn (the same permission the delete needs; hiding is not security, the delete checks it again);
 *   · not empty       → the button is greyed, its tooltip is the backend's message, and a click opens that message in a window;
 *   · empty           → red button → a window naming what goes and whether it can be undone → the delete (`sil`).
 * A 409 from the delete itself (something arrived since the check) turns the window into the same message.
 * `tikla` asks on click instead of on appearing — for a list with a button per row, so a page of 24 fairs is not 24 requests.
 * A stand has no check (`kontrol` omitted): its button is always the red one and `izin` hides it for someone without the permission.
 *
 * Give it `key={id}` when the page can show another record without unmounting it: the answer belongs to one record.
 *
 * @param {"fuar"|"firma"|"kisi"|"stand"} tur  picks the texts (components:kaliciSil.<tur>)
 * @param {string}   ad          the name shown in the window
 * @param {Function} [kontrol]   () => Promise<response body of the check endpoint>
 * @param {Function} sil         () => Promise — the delete; a rejection is shown in the window
 * @param {Function} [onSilindi] called after the delete succeeded (navigate away / reload)
 * @param {boolean}  [izin]      false → nothing is drawn (stand: `useYetki(...) === true`)
 * @param {string}   [engel]     a reason the page already knows (the fair selected in the top bar): greyed with it, no check is made
 * @param {string}   [onayMetni] when given the window asks to type it before the delete is enabled (the company)
 * @param {boolean}  [simgeSadece] icon only (table rows, cards)
 * @param {boolean}  [tikla]     ask when clicked instead of when shown
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import { Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

const DURUM = { BEKLE: "bekle", KONTROL: "kontrol", BOS: "bos", DOLU: "dolu", HATA: "hata", YOK: "yok" };

function kontrolSonucu(body) {
  const veri = body?.data ?? {};
  return { mesaj: body?.message || "", engeller: Array.isArray(veri.engeller) ? veri.engeller : [], silinebilir: veri.silinebilir === true };
}

function hataMetni(e, yedek) {
  const data = e?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  return data?.message || e?.message || yedek;
}

export default function KontrolluSilDugmesi({
  tur, ad, kontrol, sil, onSilindi, izin = true, engel = null, onayMetni = null,
  simgeSadece = false, tikla = false, size = "sm", className,
}) {
  const { t } = useTranslation("components");
  const kontrolVar = typeof kontrol === "function";
  const kontrolRef = useRef(kontrol);
  useEffect(() => { kontrolRef.current = kontrol; });

  const [durum, setDurum] = useState(kontrolVar && !tikla ? DURUM.KONTROL : DURUM.BEKLE);
  const [sonuc, setSonuc] = useState({ mesaj: "", engeller: [] });
  const [acik, setAcik] = useState(false);
  const [yazilan, setYazilan] = useState("");
  const [siliniyor, setSiliniyor] = useState(false);
  const [hata, setHata] = useState(null);
  const sira = useRef(0); // click-time checks only: a slow answer of an older click must not overwrite a newer one

  const sonucuYaz = useCallback((body) => {
    const s = kontrolSonucu(body);
    setSonuc({ mesaj: s.mesaj, engeller: s.engeller });
    setDurum(s.silinebilir ? DURUM.BOS : DURUM.DOLU);
  }, []);

  const hataYaz = useCallback((e, gizle) => {
    const durumKodu = e?.response?.status;
    if (gizle && (durumKodu === 401 || durumKodu === 403 || durumKodu === 404)) { setDurum(DURUM.YOK); return; }
    setSonuc({ mesaj: hataMetni(e, i18n.t("components:kaliciSil.kontrolEdilemedi")), engeller: [] });
    setDurum(DURUM.HATA);
  }, []);

  // Asked once when the button appears. The effect only starts the request; the state is set when it answers.
  useEffect(() => {
    if (!izin || engel || tikla || !kontrolVar) return undefined;
    let iptal = false;
    kontrolRef.current()
      .then((body) => { if (!iptal) sonucuYaz(body); })
      .catch((e) => { if (!iptal) hataYaz(e, true); });
    return () => { iptal = true; };
  }, [izin, engel, tikla, kontrolVar, sonucuYaz, hataYaz]);

  if (!izin || durum === DURUM.YOK) return null;

  const etkinDurum = engel ? DURUM.DOLU : durum;
  const mesaj = engel || sonuc.mesaj;
  const engelli = etkinDurum === DURUM.DOLU || etkinDurum === DURUM.HATA;
  const sorgulaniyor = etkinDurum === DURUM.KONTROL;
  const onayBekliyor = etkinDurum === DURUM.BOS || etkinDurum === DURUM.BEKLE;

  function ac() {
    setHata(null);
    setYazilan("");
    setAcik(true);
    if (tikla && kontrolVar && !engel) {
      const benim = ++sira.current;
      setDurum(DURUM.KONTROL);
      kontrolRef.current()
        .then((body) => { if (benim === sira.current) sonucuYaz(body); })
        .catch((e) => { if (benim === sira.current) hataYaz(e, false); });
    }
  }

  async function onayla() {
    setSiliniyor(true);
    setHata(null);
    try {
      await sil();
      setAcik(false);
      if (onSilindi) onSilindi();
    } catch (e) {
      if (e?.response?.status === 409 && Array.isArray(e.response.data?.data?.engeller)) sonucuYaz(e.response.data);
      else setHata(hataMetni(e, t("kaliciSil.silinemedi")));
    } finally {
      setSiliniyor(false);
    }
  }

  const kapat = () => { if (!siliniyor) setAcik(false); };
  const yaziTamam = !onayMetni || yazilan.trim() === onayMetni;

  return (
    // The window is a portal, but React events still bubble through it: stop them here so a click on a table row's or a card's own
    // handler (open the record) does not fire from inside this button or its window.
    <span className="d-inline-flex" onClick={(e) => e.stopPropagation()}>
      <Button
        color={engelli ? "secondary" : "danger"} outline size={size}
        className={`${engelli ? "opacity-50" : ""} ${className || ""}`.trim()}
        disabled={sorgulaniyor && !acik}
        aria-disabled={engelli || undefined}
        title={engelli ? mesaj : t(`kaliciSil.${tur}.buton`)}
        onClick={ac}
        data-testid={`sil-${tur}`}
      >
        {sorgulaniyor && !acik ? <Spinner size="sm" /> : <Trash2 size={14} className={simgeSadece ? "" : "me-1"} />}
        {!simgeSadece && t(`kaliciSil.${tur}.buton`)}
      </Button>

      <Modal isOpen={acik} toggle={kapat}>
        <ModalHeader toggle={kapat} className={onayBekliyor ? "text-danger" : ""}>
          {onayBekliyor ? t(`kaliciSil.${tur}.baslik`) : etkinDurum === DURUM.HATA ? t("common:hata") : t("kaliciSil.silinemez")}
        </ModalHeader>
        <ModalBody>
          {sorgulaniyor && (
            <div className="text-center py-3"><Spinner size="sm" className="me-2" />{t("kaliciSil.kontrolEdiliyor")}</div>
          )}
          {engelli && <p className="mb-0">{mesaj}</p>}
          {onayBekliyor && (
            <>
              <Alert color="danger" className="small" fade={false}>{t(`kaliciSil.${tur}.govde`, { ad })}</Alert>
              {onayMetni && (
                <>
                  <Label className="small">{t("kaliciSil.onayIcinYazin")}{" "}<strong>{onayMetni}</strong></Label>
                  <Input bsSize="sm" value={yazilan} onChange={(e) => setYazilan(e.target.value)} />
                </>
              )}
            </>
          )}
          {hata && <Alert color="danger" className="small py-2 mt-2 mb-0" fade={false}>{hata}</Alert>}
        </ModalBody>
        <ModalFooter>
          {onayBekliyor ? (
            <>
              <Button color="secondary" outline onClick={kapat} disabled={siliniyor}>{t("kaliciSil.vazgec")}</Button>
              <Button color="danger" onClick={onayla} disabled={siliniyor || !yaziTamam} data-testid="sil-onayla">
                {siliniyor ? <Spinner size="sm" /> : t(`kaliciSil.${tur}.onayla`)}
              </Button>
            </>
          ) : (
            <Button color="secondary" outline onClick={kapat}>{t("kaliciSil.kapat")}</Button>
          )}
        </ModalFooter>
      </Modal>
    </span>
  );
}
